import {
  Button,
  ButtonMode,
  ButtonSize,
  CheckboxList,
  Divider,
  Icon,
  ICONS,
  Popup,
  Tooltip,
} from '@creditsights/styleguide-web';
import { CheckboxListOption } from '@creditsights/styleguide-web/components/CheckboxList/CheckboxList.types';
import classNames from 'classnames/bind';
import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { useWindowResizeListener } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { useFetchForPrint, usePrint } from '../../../hooks/print';
import { PageComponentsMap } from '../../../models';
import { IssuerDto } from '../../../services/dtos/issuer/issuer.dto';
import { hasPermissionToPrint } from '../../../utils/permissionsUtils';
import { hasValue } from '../../../utils/typeGuards';
import { DEFAULT_COMPANY_WIDGETS_ORDER_CONFIG } from '../CompanyOverviewTab/CompanyOverviewTab.constants';
import { ORDERED_PRINTABLE_SECTIONS } from './CompanyPrintAction.constants';
import { getAvailableWidgetsByCompany } from './CompanyPrintAction.logic';
import styles from './CompanyPrintAction.module.scss';

const cx = classNames.bind(styles);

interface CompanyPrintActionProps {
  companyPrintViewRef?: RefObject<HTMLElement>;
  pageComponentsMap: PageComponentsMap | undefined;
  company: IssuerDto;
  hiddenSections: Record<string, boolean>;
  selectedSectionsForPrint: string[];
  setSelectedSectionsForPrint: (value: string[]) => void;
}

const CompanyPrintAction = ({
  companyPrintViewRef,
  pageComponentsMap = {},
  company,
  hiddenSections,
  selectedSectionsForPrint,
  setSelectedSectionsForPrint,
}: CompanyPrintActionProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const { data: userPermissions } = useFetchUserPermissions();
  const { userSubscription } = userPermissions!;
  const { isContentReadyForPrint } = useFetchForPrint();
  const canPrint = hasPermissionToPrint(userPermissions);

  const { leftSideWidgets, middleWidgets, rightSideWidgets } = DEFAULT_COMPANY_WIDGETS_ORDER_CONFIG[userSubscription];
  const allDefaultWidgets = [...leftSideWidgets, ...middleWidgets, ...rightSideWidgets];

  const isSectionVisible = (sectionId) => !hiddenSections[sectionId] && allDefaultWidgets.includes(sectionId);

  const options: CheckboxListOption[] = useMemo(() => {
    const widgets = getAvailableWidgetsByCompany(company, pageComponentsMap);

    return ORDERED_PRINTABLE_SECTIONS.filter(isSectionVisible)
      .map((sectionId) => widgets.find((section) => section.id === sectionId))
      .filter(hasValue);
  }, [pageComponentsMap, company]);

  useEffect(() => {
    setSelectedSectionsForPrint(options.map((option) => option.id));
  }, []);

  useWindowResizeListener(() => setIsOpen(false));

  useEffect(() => {
    companyPrintViewRef?.current?.querySelectorAll('img').forEach((img) => {
      img.parentElement?.classList.add('print-img-container');
    });
  }, [selectedSectionsForPrint]);

  const handlePrint = usePrint({
    title: `${company.displayName}: Overview`,
    contentRef: companyPrintViewRef?.current,
    onBeforeGetContent: () => {
      setIsOpen(false);
      setIsPrinting(true);
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const renderPrintButton = () => {
    return (
      <Tooltip content="Upgrade Your Subscription to Print" disabled={canPrint}>
        {/* This empty div is needed for tooltip to appear on a disabled button */}
        <div>
          <Button
            className="no-print to-translate"
            dataTestId="company-overview-print-button"
            type="button"
            mode={ButtonMode.SECONDARY}
            size={ButtonSize.MEDIUM}
            loading={isPrinting}
            disabled={!canPrint || isPrinting}
            onClick={() => setIsOpen((value) => !value)}
          >
            <Icon name={ICONS.Print} />
            Print
          </Button>
        </div>
      </Tooltip>
    );
  };

  return (
    <Popup
      className={cx('body')}
      trigger={renderPrintButton()}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false);
      }}
      position="bottom right"
      data-testid="company-overview-print-popup"
    >
      <CheckboxList
        dataTestId="company-overview-print-checkbox-list"
        options={options}
        checkedIds={selectedSectionsForPrint}
        onChange={setSelectedSectionsForPrint}
        maxHeight={329}
      />
      <div className={cx('footer')}>
        <Divider />
        <Button
          dataTestId="company-overview-print-popup-button"
          type="button"
          mode={ButtonMode.PRIMARY}
          onClick={handlePrint}
          disabled={!(selectedSectionsForPrint.length > 0 && isContentReadyForPrint)}
        >
          Print
        </Button>
      </div>
    </Popup>
  );
};

export default CompanyPrintAction;
